import { type MutableRefObject, useCallback, useRef, useState } from 'react';

import cx from 'classnames';
import FocusTrap from 'focus-trap-react';
import { AnimatePresence, motion } from 'framer-motion';

import Close from '~/ui/assets/icons/close.svg';
import LogoWorded from '~/ui/assets/icons/logo-worded.svg';
import SearchIcon from '~/ui/assets/icons/search.svg';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Link } from '~/ui/components/link';
import { Breakpoint } from '~/ui/styles/grid';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { Route } from '~/ui/systems/internalLink/route';

import { PrimaryNavigationItem } from './navigationItem';
import styles from './primary.module.scss';
import { type AnimationDuration, type NavigationItem } from '../navigation';

const ANIMATION_DURATION = 800;

interface PrimaryNavigationProps {
  className?: string;
  isLarge?: boolean;
  setActiveNavigation: (navigationItem: NavigationItem | undefined) => void;
  activeNavigation: NavigationItem | undefined;
  setPrimaryIsExpanded: (expanded: boolean) => void;
  primaryIsExpanded: boolean;
  isCondensed?: boolean;
  scrollLockRef?: MutableRefObject<HTMLDivElement | null>;
  onSearchClick?: () => void;
  desktopContent?: React.ReactNode;
  showDesktopContent?: boolean;
}

export function PrimaryNavigation({
  className,
  isLarge,
  setActiveNavigation,
  activeNavigation,
  primaryIsExpanded,
  setPrimaryIsExpanded,
  scrollLockRef,
  isCondensed,
  onSearchClick,
  desktopContent,
  showDesktopContent,
}: PrimaryNavigationProps) {
  const breakpoint = useBreakpoint();
  const [animationType, setAnimationType] = useState<AnimationDuration>('instant');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const togglePrimary = () => {
    timer.current && clearTimeout(timer.current);
    setAnimationType('default');
    timer.current = setTimeout(() => {
      setAnimationType('instant');
    }, ANIMATION_DURATION);
    setPrimaryIsExpanded(!primaryIsExpanded);
  };

  const isSmallOrMedium = breakpoint === Breakpoint.MOBILE || breakpoint === Breakpoint.TABLET;
  const closeNavInstant = animationType === 'instant';

  const navigationClasses = cx(styles.navigation, className, {
    [styles.navigationLarge]: isLarge,
  });

  const contentTopClasses = cx(styles.contentTop, {
    [styles.contentTopScrolled]: isCondensed,
    [styles.contentTopHidden]: !!activeNavigation,
    [styles.contentTopInstant]: !(!primaryIsExpanded && !!activeNavigation),
  });

  const contentBottomClasses = cx(styles.contentBottom, {
    [styles.contentBottomExpanded]: primaryIsExpanded,
    [styles.contentBottomInstant]: closeNavInstant,
  });

  const wordmarkLogoClasses = cx(styles.wordmarkLogo, {
    [styles.wordmarkLogoScrolled]: isCondensed,
  });

  const handleItemsRender = useCallback((items: HTMLDivElement | null) => {
    if (!items) {
      return;
    }

    const height = items.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--primary-navigation-height', `${height / 10}rem`);
  }, []);

  return (
    <>
      <FocusTrap
        active={primaryIsExpanded && isSmallOrMedium}
        focusTrapOptions={{ initialFocus: false }}
      >
        <div className={navigationClasses} id="primary-navigation" ref={scrollLockRef}>
          <div className={contentTopClasses}>
            <div className={styles.contentTopInnerWrapper}>
              <Link href={Route.HOME} className={wordmarkLogoClasses} aria-label="homepage">
                <LogoWorded />
              </Link>
              <button
                className={cx(styles.menuButton, typographyStyles.link)}
                onClick={togglePrimary}
                aria-label={primaryIsExpanded ? 'Close navigation' : undefined}
              >
                {primaryIsExpanded ? <Close className={styles.closeIcon} /> : 'Menu'}
              </button>
            </div>
          </div>
          <div className={contentBottomClasses}>
            <AnimatePresence>
              {(!isSmallOrMedium || primaryIsExpanded) && (
                <motion.div
                  className={cx(styles.contentBottomInnerWrapper, {
                    [styles.showDesktopContent]: showDesktopContent,
                  })}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{
                    opacity: 0,
                    transition: {
                      duration: 0.4,
                    },
                  }}
                  transition={isSmallOrMedium ? { delay: 0.5, duration: 0.8 } : {}}
                >
                  <div className={styles.alternativeContent}>{desktopContent}</div>
                  <div className={styles.defaultContent}>
                    <div className={styles.navigationItemsWrapper} ref={handleItemsRender}>
                      <PrimaryNavigationItem
                        item="ideas"
                        activeNavigation={activeNavigation}
                        setActiveNavigation={setActiveNavigation}
                      />
                      <PrimaryNavigationItem
                        item="pages"
                        activeNavigation={activeNavigation}
                        setActiveNavigation={setActiveNavigation}
                      />
                      <PrimaryNavigationItem
                        item="resources"
                        activeNavigation={activeNavigation}
                        setActiveNavigation={setActiveNavigation}
                      />
                      <PrimaryNavigationItem
                        item="search"
                        activeNavigation={activeNavigation}
                        setActiveNavigation={setActiveNavigation}
                        icon={SearchIcon}
                        onClick={onSearchClick}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </FocusTrap>
    </>
  );
}
